/**
 * Badge Component
 *
 * Status indicators and labels with Kleda styling.
 */

import { type HTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'orange';
  size?: 'sm' | 'md';
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', size = 'md', className, children, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center font-medium rounded-full';

    const variants = {
      default: 'bg-gray-100 text-gray-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      danger: 'bg-red-100 text-red-800',
      info: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
    };

    const sizes = {
      sm: 'px-xs py-0.5 text-xs',
      md: 'px-sm py-xs text-sm',
    };

    return (
      <span
        ref={ref}
        className={clsx(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// Predefined status badges for work orders
export function WorkOrderStatusBadge({
  status,
  className,
}: {
  status: string;
  className?: string;
}) {
  const getVariant = (): BadgeProps['variant'] => {
    switch (status) {
      case 'Opgehaald':
      case 'Teruggebracht':
        return 'success';
      case 'Ophalen ingepland':
      case 'Terugbrengen ingepland':
        return 'info';
      case 'Aan het reinigen':
        return 'warning';
      case 'Ophalen plannen':
      case 'Terugbrengen plannen':
        return 'default';
      default:
        return 'default';
    }
  };

  // Translate status to shorter display text
  const getDisplayText = (): string => {
    const statusMap: Record<string, string> = {
      'Ophalen plannen': 'Te plannen',
      'Ophaalvoorstel gedaan': 'Voorstel verstuurd',
      'Ophaalvoorstel geen reactie': 'Geen reactie',
      'Ophalen ingepland': 'Ingepland',
      'Opgehaald': 'Opgehaald',
      'Aan het reinigen': 'In behandeling',
      'Terugbrengen plannen': 'Te plannen',
      'Aflevervoorstel gedaan': 'Voorstel verstuurd',
      'Terugbrengen ingepland': 'Ingepland',
      'Teruggebracht': 'Afgeleverd',
    };
    return statusMap[status] || status;
  };

  return (
    <Badge variant={getVariant()} className={className}>
      {getDisplayText()}
    </Badge>
  );
}
